import React, { useState } from 'react';
import { useDb } from '../db/useDataService';
import * as ds from '../db/dataService';

import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  CopyOutlined,
  LinkOutlined,
} from '@ant-design/icons';
import {
  Button,
  Table,
  Tag,
  Space,
  Popconfirm,
  Tooltip,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  Typography,
  message,
} from 'antd';
import { ColumnsType } from 'antd/lib/table';

import {
  Offer,
  ProductTag,
  CreateOfferFormValues,
  LifeSphere,
  OfferBinding,
  OfferBindingScope,
} from '../types';
import OfferBindingTree from './OfferBindingTree';

const { Text } = Typography;

// --- Predefined page goals ---

const PAGE_GOALS = [
  'Купить курс',
  'Регистрация на вебинар',
  'Регистрация на марафон',
  'Заявка на консультацию',
  'Купить миникурс, трипваер',
  'Получить доступ к чек-листу',
  'Получить доступ к серии уроков',
  'Подписаться в TG канал',
  'Подписаться в бота',
];

// --- Mock data ---

const mockOffers: Offer[] = [
  {
    id: '1',
    productId: '1',
    name: 'Бесплатный вебинар',
    keyPromise: 'Узнайте 5 способов привлечения клиентов за 1.5 часа',
    averageCheck: 0,
    address: {
      id: 'a1',
      name: 'Лендинг вебинара',
      url: 'https://example.com/webinar-marketing',
      pageGoal: 'Регистрация на вебинар',
      pageText: '',
    },
    metrikaConversionId: '12345678',
    desiredConversionCost: 150,
    rkBindings: [
      {
        id: 'b1',
        rkId: 'rk1',
        rkName: 'Директ Основной',
        rkSource: 'yandex_direct',
        rules: [{ id: 'r1', type: 'contains', value: 'вебинар' }],
        utmTemplate: {
          utm_source: 'yandex',
          utm_medium: 'cpc',
          utm_campaign: '{campaign_name}',
          utm_content: '{ad_id}',
          utm_term: '{keyword}',
        },
      },
    ],
    tags: [],
    createdAt: '2026-01-20T10:00:00Z',
  },
  {
    id: '2',
    productId: '1',
    name: 'Прямая продажа курса',
    keyPromise: 'Курс по маркетингу со скидкой 40% только до конца недели',
    averageCheck: 5990,
    address: {
      id: 'a2',
      name: 'Продающий лендинг',
      url: 'https://example.com/marketing-sale',
      pageGoal: 'Купить курс',
      pageText: '',
    },
    metrikaConversionId: '87654321',
    desiredConversionCost: 800,
    rkBindings: [
      {
        id: 'b2',
        rkId: 'rk1',
        rkName: 'Директ Основной',
        rkSource: 'yandex_direct',
        rules: [
          { id: 'r2', type: 'contains', value: 'прямая' },
          { id: 'r3', type: 'campaign_id', value: '99001' },
        ],
        utmTemplate: null,
      },
    ],
    tags: [],
    createdAt: '2026-02-01T14:00:00Z',
  },
  {
    id: '3',
    productId: '1',
    name: 'Бесплатный чек-лист',
    keyPromise: '10 шагов к первым клиентам — скачайте чек-лист',
    averageCheck: 0,
    address: {
      id: 'a3',
      name: 'Страница чек-листа',
      url: 'https://example.com/checklist',
      pageGoal: 'Получить доступ к чек-листу',
      pageText: '',
    },
    metrikaConversionId: '',
    desiredConversionCost: 50,
    rkBindings: [],
    tags: [],
    createdAt: '2026-03-05T09:00:00Z',
  },
];

// --- Component ---

interface OffersTabProps {
  productId: string;
  productName?: string;
  spheres?: LifeSphere[];
}

const RULE_TYPE_LABELS: Record<string, string> = {
  all: 'Все кампании',
  contains: 'Содержит',
  campaign_id: 'ID кампании',
  exact_name: 'Точное название',
};

const OffersTab: React.FC<OffersTabProps> = ({ productId, productName = 'продукт', spheres = [] }) => {
  const db = useDb();
  const offers: Offer[] = db.offers.filter((o) => o.productId === productId).map((o) => {
    const addr = db.offerAddresses.find((a) => a.offerId === o.id);
    return {
      id: o.id,
      productId: o.productId,
      name: o.name,
      description: o.description,
      keyPromise: o.keyPromise,
      averageCheck: o.averageCheck,
      desiredConversionCost: o.desiredConversionCost,
      metrikaConversionId: o.metrikaConversionId,
      address: addr ? { id: addr.id, name: addr.name, url: addr.url, pageGoal: addr.pageGoal, pageText: addr.pageText || '' } : undefined,
      rkBindings: [],
      bindings: db.offerBindings.filter((b) => b.offerId === o.id).map((b) => ({ scope: b.scope, scopeId: b.scopeId })),
      tags: [],
      createdAt: o.createdAt,
    };
  });
  const setOffers = (_: Offer[]) => { /* noop — данные живут в dataService */ };
  const [modalVisible, setModalVisible] = useState(false);
  const [editingOffer, setEditingOffer] = useState<Offer | null>(null);
  const [bindings, setBindings] = useState<OfferBinding[]>([]);
  const [form] = Form.useForm<CreateOfferFormValues>();

  const handleCreate = () => {
    setEditingOffer(null);
    setBindings([]);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (offer: Offer) => {
    setEditingOffer(offer);
    setBindings(offer.bindings || []);
    form.setFieldsValue({
      name: offer.name,
      keyPromise: offer.keyPromise,
      averageCheck: offer.averageCheck,
      addressName: offer.address?.name || '',
      addressUrl: offer.address?.url || '',
      addressPageGoal: offer.address?.pageGoal || '',
      metrikaConversionId: offer.metrikaConversionId,
      desiredConversionCost: offer.desiredConversionCost,
    });
    setModalVisible(true);
  };

  const handleSubmit = () => {
    form.validateFields().then((values) => {
      if (editingOffer) {
        ds.updateOffer(editingOffer.id, {
          name: values.name,
          keyPromise: values.keyPromise,
          averageCheck: values.averageCheck,
          metrikaConversionId: values.metrikaConversionId,
          desiredConversionCost: values.desiredConversionCost,
        });
        ds.setOfferBindings(editingOffer.id, bindings);
        message.success('Оффер обновлён');
      } else {
        const created = ds.createOffer({
          productId,
          name: values.name,
          keyPromise: values.keyPromise,
          averageCheck: values.averageCheck,
          metrikaConversionId: values.metrikaConversionId || '',
          desiredConversionCost: values.desiredConversionCost,
        });
        ds.setOfferBindings(created.id, bindings);
        message.success('Оффер создан');
      }
      setModalVisible(false);
      form.resetFields();
    });
  };

  const handleDelete = (id: string) => {
    ds.deleteOffer(id);
    message.success('Оффер удалён');
  };

  const handleDuplicate = (offer: Offer) => {
    const created = ds.createOffer({
      productId: offer.productId,
      name: `${offer.name} (копия)`,
      description: offer.description,
      keyPromise: offer.keyPromise,
      averageCheck: offer.averageCheck,
      metrikaConversionId: offer.metrikaConversionId,
      desiredConversionCost: offer.desiredConversionCost,
    });
    ds.setOfferBindings(created.id, offer.bindings || []);
    message.success('Оффер дублирован');
  };

  const columns: ColumnsType<Offer> = [
    {
      title: 'Название',
      dataIndex: 'name',
      key: 'name',
      render: (name: string, record: Offer) => (
        <div>
          <a onClick={() => handleEdit(record)} style={{ fontWeight: 500 }}>
            {name}
          </a>
          <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 2 }}>
            {record.keyPromise}
          </div>
        </div>
      ),
    },
    {
      title: 'Страница',
      key: 'address',
      width: 220,
      render: (_: unknown, record: Offer) => (
        <div>
          <div style={{ fontSize: 13 }}>{record.address?.name || <span style={{ color: '#bfbfbf' }}>—</span>}</div>
          {record.address?.pageGoal && (
            <div style={{ fontSize: 12, color: '#8c8c8c' }}>
              <Tag style={{ fontSize: 11 }}>{record.address.pageGoal}</Tag>
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Привязки',
      key: 'bindings',
      width: 260,
      render: (_: unknown, record: Offer) => {
        const bs = record.bindings || [];
        if (bs.length === 0) return <span style={{ color: '#bfbfbf' }}>—</span>;
        const pathOf = (b: OfferBinding): string => {
          if (b.scope === 'product') return 'Весь продукт';
          for (const s of spheres) {
            if (b.scope === 'sphere' && s.id === b.scopeId) return s.name;
            for (const c of s.themes) {
              if (b.scope === 'theme' && c.id === b.scopeId) return `${s.name} / ${c.name}`;
              for (const t of c.subthemes) {
                if (b.scope === 'subtheme' && t.id === b.scopeId) return `${s.name} / ${c.name} / ${t.name}`;
              }
            }
          }
          return '—';
        };
        const colorOf: Record<OfferBindingScope, string> = { product: 'magenta', sphere: 'blue', theme: 'cyan', subtheme: 'geekblue' };
        return (
          <Space size={[4, 4]} wrap>
            {bs.slice(0, 4).map((b, i) => (
              <Tag key={i} color={colorOf[b.scope]} style={{ fontSize: 11 }}>{pathOf(b)}</Tag>
            ))}
            {bs.length > 4 && <Tag style={{ fontSize: 11 }}>+{bs.length - 4}</Tag>}
          </Space>
        );
      },
    },
    {
      title: 'Средний чек',
      dataIndex: 'averageCheck',
      key: 'averageCheck',
      width: 120,
      render: (value: number | null) => {
        if (!value) return <span style={{ color: '#bfbfbf' }}>—</span>;
        return `${value.toLocaleString('ru-RU')} ₽`;
      },
    },
    {
      title: 'Цель конверсии',
      dataIndex: 'desiredConversionCost',
      key: 'desiredConversionCost',
      width: 130,
      render: (value: number | null) => {
        if (!value) return <span style={{ color: '#bfbfbf' }}>—</span>;
        return `${value.toLocaleString('ru-RU')} ₽`;
      },
    },
    {
      title: 'РК',
      key: 'rkBindings',
      width: 200,
      render: (_: unknown, record: Offer) => {
        if (!record.rkBindings || record.rkBindings.length === 0) {
          return <span style={{ color: '#bfbfbf' }}>Не привязаны</span>;
        }
        return (
          <Space size={[4, 4]} wrap>
            {record.rkBindings.map((binding) => (
              <Tooltip
                key={binding.id}
                title={
                  <div>
                    {binding.rules.map((rule) => (
                      <div key={rule.id}>
                        {RULE_TYPE_LABELS[rule.type]}: {rule.type === 'all' ? '' : rule.value}
                      </div>
                    ))}
                  </div>
                }
              >
                <Tag>
                  <LinkOutlined style={{ marginRight: 4 }} />
                  {binding.rkName}
                </Tag>
              </Tooltip>
            ))}
          </Space>
        );
      },
    },
    {
      title: '',
      key: 'actions',
      width: 120,
      render: (_: unknown, record: Offer) => (
        <Space size={4}>
          <Tooltip title="Редактировать">
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Tooltip title="Дублировать">
            <Button
              type="text"
              size="small"
              icon={<CopyOutlined />}
              onClick={() => handleDuplicate(record)}
            />
          </Tooltip>
          <Tooltip title="Удалить">
            <Popconfirm
              title="Удалить оффер?"
              onConfirm={() => handleDelete(record.id)}
              okText="Да"
              cancelText="Нет"
            >
              <Button type="text" size="small" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div className="product-page__tab-content">
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <Text strong style={{ fontSize: 16 }}>
          Офферы ({offers.length})
        </Text>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleCreate}
          style={{
            backgroundColor: '#ff4d4f',
            borderColor: '#ff4d4f',
            borderRadius: 8,
          }}
        >
          Добавить оффер
        </Button>
      </div>

      <Table
        rowKey="id"
        columns={columns}
        dataSource={offers}
        pagination={false}
        locale={{ emptyText: 'Нет офферов' }}
        style={{ border: '1px solid #f0f0f0', borderRadius: 8 }}
      />

      <Modal
        title={editingOffer ? 'Редактировать оффер' : 'Создать оффер'}
        visible={modalVisible}
        onOk={handleSubmit}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        okText={editingOffer ? 'Сохранить' : 'Создать'}
        cancelText="Отмена"
        width={960}
        destroyOnClose
      >
        <Form form={form} layout="vertical" requiredMark={false}>
          <Form.Item
            name="name"
            label="Название оффера"
            rules={[{ required: true, message: 'Введите название' }]}
          >
            <Input placeholder="Например: Бесплатный вебинар" />
          </Form.Item>
          <div
            style={{
              background: '#fafafa',
              border: '1px solid #f0f0f0',
              borderRadius: 8,
              padding: 16,
              marginBottom: 16,
            }}
          >
            <Text strong style={{ display: 'block', marginBottom: 4 }}>
              Привязки оффера
            </Text>
            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 12 }}>
              Отметьте узлы, к которым относится оффер. Дети выбранного узла наследуют привязку автоматически.
            </Text>
            <OfferBindingTree
              spheres={spheres}
              productId={productId}
              productName={productName}
              value={bindings}
              onChange={setBindings}
              treeHeight={260}
            />
          </div>
          <Form.Item
            name="keyPromise"
            label="Ключевое обещание"
            rules={[{ required: true, message: 'Введите ключевое обещание' }]}
          >
            <Input.TextArea rows={2} placeholder="Что получит человек" />
          </Form.Item>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <Form.Item
              name="averageCheck"
              label="Средний чек (₽)"
            >
              <InputNumber
                style={{ width: '100%' }}
                min={0}
                placeholder="0"
              />
            </Form.Item>
            <Form.Item
              name="desiredConversionCost"
              label="Желаемая стоимость конверсии (₽)"
            >
              <InputNumber
                style={{ width: '100%' }}
                min={0}
                placeholder="0"
              />
            </Form.Item>
          </div>
          <div
            style={{
              background: '#fafafa',
              border: '1px solid #f0f0f0',
              borderRadius: 8,
              padding: 16,
              marginBottom: 16,
            }}
          >
            <Text strong style={{ display: 'block', marginBottom: 12 }}>
              Адрес страницы
            </Text>
            <Form.Item
              name="addressName"
              label="Название страницы"
              rules={[{ required: true, message: 'Введите название' }]}
            >
              <Input placeholder="Например: Лендинг вебинара" />
            </Form.Item>
            <Form.Item
              name="addressUrl"
              label="URL адрес"
              rules={[
                { required: true, message: 'Введите URL' },
                { type: 'url', message: 'Введите корректный URL' },
              ]}
            >
              <Input placeholder="https://example.com/page" />
            </Form.Item>
            <Form.Item
              name="addressPageGoal"
              label="Задача страницы"
              rules={[{ required: true, message: 'Выберите задачу' }]}
            >
              <Select
                placeholder="Выберите задачу страницы"
                showSearch
                filterOption={(input, option) =>
                  String(option?.children || '').toLowerCase().includes(input.toLowerCase())
                }
              >
                {PAGE_GOALS.map((goal) => (
                  <Select.Option key={goal} value={goal}>
                    {goal}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </div>
          <Form.Item
            name="metrikaConversionId"
            label="ID конверсии Яндекс Метрики"
          >
            <Input placeholder="Например: 12345678" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default OffersTab;
